import React, { useState, useEffect } from 'react';
import { useLocalization } from '../contexts/LocalizationContext';
import { useAuth } from '../contexts/AuthContext';
import LoadingScreen from './LoadingScreen';
import { 
  Search, 
  Filter, 
  Download, 
  Calendar, 
  Eye, 
  CheckCircle, 
  XCircle, 
  Clock,
  CreditCard,
  RefreshCw,
  X,
  Copy,
  User,
  Mail,
  FileText,
  DollarSign,
  CreditCard as CardIcon,
  Calendar as CalendarIcon,
  Clock as TimeIcon,
  Hash,
  Info,
  AlertCircle,
  TrendingDown,
  ShieldCheck
} from 'lucide-react';
import { cardService, CardTransaction } from '../services/cardService';

const CardTransactions: React.FC = () => {
  const { t, formatCurrency, formatDate } = useLocalization();
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<CardTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [exporting, setExporting] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<CardTransaction | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Fonction pour afficher un toast
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Fonction pour copier dans le presse-papier
  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      showToast('Copied to clipboard!');
    }).catch(() => {
      showToast('Failed to copy', 'error');
    });
  };

  // Fonction pour ouvrir la modal de détails
  const handleViewDetails = (transaction: CardTransaction) => {
    setSelectedTransaction(transaction);
    setShowDetailsModal(true);
  };

  const formatField = (value: any, fallback: string = 'N/A'): string => {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }
    return String(value);
  };

  const fetchTransactions = async (page = 1) => {
    try {
      setLoading(true);
      const response = await cardService.getCardTransactions({
        page,
        limit: pagination.limit,
        search: searchTerm,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        start_date: dateRange.start || undefined,
        end_date: dateRange.end || undefined
      });

      setTransactions(response.transactions);
      setPagination({
        page: response.page,
        limit: response.limit,
        total: response.total,
        totalPages: response.total_pages
      });
      setError(null);
    } catch (err: any) {
      console.error('Error fetching transactions:', err);
      setError(err.response?.data?.detail || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await cardService.exportCardTransactions({
        search: searchTerm,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        start_date: dateRange.start || undefined,
        end_date: dateRange.end || undefined
      });

      const filename = cardService.formatFilename('card-transactions');
      cardService.downloadExport(blob, filename);
      showToast('Export completed successfully!');
      
    } catch (err: any) {
      console.error('Export error:', err);
      showToast('Failed to export data', 'error');
    } finally {
      setExporting(false);
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setDateRange({ start: '', end: '' });
  };

  useEffect(() => {
    fetchTransactions();
  }, [searchTerm, statusFilter, dateRange]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SUCCESS': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'FAILED': return 'bg-red-100 text-red-800 border-red-200';
      case 'PENDING': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'CANCELLED': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'SUCCESS': return <CheckCircle className="w-4 h-4" />;
      case 'FAILED': return <XCircle className="w-4 h-4" />;
      case 'PENDING': return <Clock className="w-4 h-4" />;
      default: return null;
    }
  };

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'SUCCESS', label: 'Successful' },
    { value: 'FAILED', label: 'Failed' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'CANCELLED', label: 'Cancelled' }
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-xl shadow-xl transform transition-all duration-300 animate-slide-in ${
          toast.type === 'success'
            ? 'bg-emerald-500 text-white border border-emerald-600'
            : 'bg-red-500 text-white border border-red-600'
        }`}>
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-white"></div>
            <span>{toast.message}</span>
          </div>
        </div>
      )}

        {/* Header Banner */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-5 sm:p-6 text-white">
          <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)", backgroundSize: "28px 28px" }} />
          <div className="absolute -top-32 -right-20 w-96 h-96 bg-emerald-500/20 rounded-full blur-[100px]" />
          <div className="absolute -bottom-32 -left-20 w-96 h-96 bg-teal-500/15 rounded-full blur-[100px]" />

          <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl">
                <CreditCard className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight">Card Transactions</h1>
                <p className="text-slate-400 mt-1 text-sm">
                  Monitor and analyze all your payment card transactions
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={resetFilters}
                disabled={loading}
                className="px-4 py-2.5 border border-white/10 bg-white/[0.06] rounded-xl text-slate-300 hover:bg-white/10 transition-all flex items-center gap-2 disabled:opacity-50 text-sm font-medium"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Reset
              </button>
              <button
                onClick={() => fetchTransactions()}
                disabled={loading}
                className="px-4 py-2.5 border border-white/10 bg-white/[0.06] rounded-xl text-slate-300 hover:bg-white/10 transition-all flex items-center gap-2 text-sm font-medium"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>

              <button
                onClick={handleExport}
                disabled={exporting}
                className="px-4 py-2.5 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-all flex items-center gap-2 shadow-lg shadow-emerald-500/30 text-sm font-medium"
              >
                <Download className={`w-4 h-4 ${exporting ? 'animate-spin' : ''}`} />
                {exporting ? 'Exporting...' : 'Export CSV'}
              </button>
            </div>
          </div>
        </div>

        {/* Filters Card */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 mb-8">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Search */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Transactions
              </label>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by reference, email, transaction ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status Filter
              </label>
              <div className="relative">
                <Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full pl-12 pr-10 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Range
              </label>
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <span className="text-gray-400">to</span>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Table Header */}
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Transaction History</h2>
            <div className="text-[11px] text-gray-500">
              Page {pagination.page} of {pagination.totalPages} · {pagination.total} total
            </div>
          </div>

          {loading ? (
            <LoadingScreen fullscreen={false} message="Loading transactions..." />
          ) : error ? (
            <div className="p-12 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Error Loading Transactions</h3>
              <p className="text-gray-600 max-w-md mx-auto mb-6">{error}</p>
              <button
                onClick={() => fetchTransactions()}
                className="px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : transactions.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
                <CreditCard className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">No transactions found</h3>
              <p className="text-gray-600 max-w-md mx-auto mb-6">
                Try adjusting your filters or create a new transaction
              </p>
              <button
                onClick={resetFilters}
                className="px-5 py-2.5 bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all"
              >
                Clear All Filters
              </button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Transaction</th>
                      <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                      <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Net</th>
                      <th className="px-4 py-2.5 text-center text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Currency</th>
                      <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-gray-600 uppercase tracking-wider w-16"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {transactions.map((transaction) => (
                      <tr key={transaction.id} className="hover:bg-gray-50/60 transition-colors group">
                        <td className="px-4 py-2 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-xs text-gray-900">{transaction.merchant_reference}</span>
                            <button
                              onClick={() => handleCopyToClipboard(transaction.merchant_reference)}
                              className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600 transition-all"
                              title="Copy reference"
                            >
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                          <div className="text-[10px] text-gray-400 font-mono truncate max-w-[260px]">
                            {transaction.transaction_uuid}
                          </div>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-right">
                          <span className="text-xs font-semibold text-gray-900 tabular-nums">
                            {Number(transaction.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-right">
                          <span className="text-xs text-gray-700 tabular-nums">
                            {Number(transaction.merchant_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          {transaction.total_commission_amount > 0 && (
                            <span className="ml-1 text-[10px] text-rose-500 tabular-nums">
                              (-{Number(transaction.total_commission_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-center">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono font-semibold text-gray-700 bg-gray-100 border border-gray-200">
                            {transaction.currency}
                          </span>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border ${getStatusColor(transaction.status)}`}>
                            {transaction.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <div className="text-xs text-gray-700">{formatDate(transaction.created_at)}</div>
                          <div className="text-[10px] text-gray-400">
                            {new Date(transaction.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-right">
                          <button
                            onClick={() => handleViewDetails(transaction)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors"
                            title="View details"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Modal de détails */}
              {showDetailsModal && selectedTransaction && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                  <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                    {/* Overlay */}
                    <div 
                      className="fixed inset-0 transition-opacity bg-gray-900/70" 
                      onClick={() => setShowDetailsModal(false)}
                    ></div>

                    {/* Modal */}
                    {/* Modal de détails */}
                    {showDetailsModal && selectedTransaction && (
                    <div className="fixed inset-0 z-50 overflow-y-auto">
                        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                        {/* Overlay */}
                        <div 
                            className="fixed inset-0 transition-opacity bg-gray-900/70" 
                            onClick={() => setShowDetailsModal(false)}
                        ></div>

                        {/* Modal */}
                        <div className="inline-block w-full max-w-5xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-2xl shadow-2xl">
                            {/* Header */}
                            <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                <div className="p-3 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl">
                                    <CardIcon className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">
                                    Transaction Details
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                    ID: {selectedTransaction.id}
                                    </p>
                                </div>
                                </div>
                                <button
                                onClick={() => setShowDetailsModal(false)}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                <X className="w-6 h-6" />
                                </button>
                            </div>
                            </div>

                            {/* Content */}
                            <div className="px-8 py-6">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                {/* Left Column - Basic Info */}
                                <div className="space-y-6">
                                {/* Status Card */}
                                <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-6">
                                    <h4 className="text-sm font-semibold text-gray-600 mb-4">Transaction Status</h4>
                                    <div className="flex items-center gap-4">
                                    <div className={`p-3 rounded-lg ${getStatusColor(selectedTransaction.status)}`}>
                                        {getStatusIcon(selectedTransaction.status)}
                                    </div>
                                    <div>
                                        <div className="text-lg font-bold text-gray-900">
                                        {selectedTransaction.status}
                                        </div>
                                        {selectedTransaction.decision && (
                                        <div className="text-sm text-gray-600 mt-1">
                                            {selectedTransaction.decision}
                                        </div>
                                        )}
                                    </div>
                                    </div>
                                </div>

                                {/* Timeline */}
                                <div className="space-y-4">
                                    <h4 className="text-sm font-semibold text-gray-600">Timeline</h4>
                                    <div className="space-y-3">
                                    <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                                        <div className="flex items-center gap-3">
                                        <CalendarIcon className="w-5 h-5 text-blue-500" />
                                        <div>
                                            <div className="text-sm font-medium text-gray-900">Created</div>
                                            <div className="text-xs text-gray-500">{formatDate(selectedTransaction.created_at)}</div>
                                        </div>
                                        </div>
                                        <div className="text-sm text-gray-600">
                                        {new Date(selectedTransaction.created_at).toLocaleTimeString()}
                                        </div>
                                    </div>
                                    {selectedTransaction.updated_at !== selectedTransaction.created_at && (
                                        <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <TimeIcon className="w-5 h-5 text-purple-500" />
                                            <div>
                                            <div className="text-sm font-medium text-gray-900">Updated</div>
                                            <div className="text-xs text-gray-500">{formatDate(selectedTransaction.updated_at)}</div>
                                            </div>
                                        </div>
                                        <div className="text-sm text-gray-600">
                                            {new Date(selectedTransaction.updated_at).toLocaleTimeString()}
                                        </div>
                                        </div>
                                    )}
                                    </div>
                                </div>

                                {/* References */}
                                <div className="space-y-4">
                                    <h4 className="text-sm font-semibold text-gray-600">References</h4>
                                    <div className="space-y-3">
                                    <div className="p-3 bg-gray-50 rounded-lg">
                                        <div className="flex items-center justify-between mb-2">
                                        <div className="text-xs font-medium text-gray-500">Transaction UUID</div>
                                        <button
                                            onClick={() => handleCopyToClipboard(selectedTransaction.transaction_uuid)}
                                            className="text-blue-600 hover:text-blue-700 text-xs flex items-center gap-1"
                                        >
                                            <Copy className="w-3 h-3" />
                                            Copy
                                        </button>
                                        </div>
                                        <div className="font-mono text-sm text-gray-900 break-all">
                                        {selectedTransaction.transaction_uuid}
                                        </div>
                                    </div>
                                    <div className="p-3 bg-gray-50 rounded-lg">
                                        <div className="flex items-center justify-between mb-2">
                                        <div className="text-xs font-medium text-gray-500">Merchant Reference</div>
                                        <button
                                            onClick={() => handleCopyToClipboard(selectedTransaction.merchant_reference)}
                                            className="text-blue-600 hover:text-blue-700 text-xs flex items-center gap-1"
                                        >
                                            <Copy className="w-3 h-3" />
                                            Copy
                                        </button>
                                        </div>
                                        <div className="font-medium text-gray-900">
                                        {selectedTransaction.merchant_reference}
                                        </div>
                                    </div>
                                    {selectedTransaction.reconciliation_reference_number && (
                                    <div className="p-3 bg-gray-50 rounded-lg">
                                        <div className="flex items-center justify-between mb-2">
                                        <div className="text-xs font-medium text-gray-500">Reconciliation Ref</div>
                                        <button
                                            onClick={() => handleCopyToClipboard(selectedTransaction.reconciliation_reference_number!)}
                                            className="text-blue-600 hover:text-blue-700 text-xs flex items-center gap-1"
                                        >
                                            <Copy className="w-3 h-3" />
                                            Copy
                                        </button>
                                        </div>
                                        <div className="font-medium text-gray-900">
                                        {selectedTransaction.reconciliation_reference_number}
                                        </div>
                                    </div>
                                    )}
                                    </div>
                                </div>
                                </div>

                                {/* Middle Column - Amounts & Description */}
                                <div className="space-y-6">
                                {/* Amounts */}
                                <div className="space-y-4">
                                    <h4 className="text-sm font-semibold text-gray-600">Amount Details</h4>
                                    <div className="space-y-4">
                                    <div className="p-4 bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-xl">
                                        <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <DollarSign className="w-5 h-5 text-blue-600" />
                                            <span className="text-sm font-medium text-gray-700">Amount</span>
                                        </div>
                                        <span className="text-xl font-bold text-blue-700">
                                            {formatCurrency(selectedTransaction.amount, selectedTransaction.currency)}
                                        </span>
                                        </div>
                                        <div className="text-xs text-gray-500">Original transaction amount</div>
                                    </div>
                                    <div className="p-4 bg-gradient-to-r from-emerald-50 to-emerald-100 border border-emerald-200 rounded-xl">
                                        <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <ShieldCheck className="w-5 h-5 text-emerald-600" />
                                            <span className="text-sm font-medium text-gray-700">Net Amount</span>
                                        </div>
                                        <span className="text-xl font-bold text-emerald-700">
                                            {formatCurrency(selectedTransaction.merchant_amount, selectedTransaction.currency)}
                                        </span>
                                        </div>
                                        <div className="text-xs text-gray-500">Amount after commissions</div>
                                    </div>
                                    {selectedTransaction.total_commission_amount > 0 && (
                                        <div className="p-4 bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-xl">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                            <TrendingDown className="w-5 h-5 text-red-600" />
                                            <span className="text-sm font-medium text-gray-700">Commission</span>
                                            </div>
                                            <span className="text-xl font-bold text-red-700">
                                            {formatCurrency(selectedTransaction.total_commission_amount, selectedTransaction.currency)}
                                            </span>
                                        </div>
                                        <div className="text-xs text-gray-500">Total commission deducted</div>
                                        </div>
                                    )}
                                    </div>
                                </div>

                                {/* Description Section */}
                                {selectedTransaction.description && (
                                    <div className="space-y-4">
                                    <h4 className="text-sm font-semibold text-gray-600">Description</h4>
                                    <div className="p-4 bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200 rounded-xl">
                                        <div className="flex items-center gap-2 mb-3">
                                        <FileText className="w-5 h-5 text-amber-600" />
                                        <span className="text-sm font-medium text-gray-700">Transaction Description</span>
                                        </div>
                                        <div className="text-sm text-gray-700 whitespace-pre-wrap bg-white/50 p-3 rounded-lg border border-amber-100">
                                        {selectedTransaction.description}
                                        </div>
                                    </div>
                                    </div>
                                )}
                                </div>

                                {/* Right Column - Customer & Merchant Info */}
                                <div className="space-y-6">
                                {/* Customer Information */}
                                <div className="space-y-4">
                                    <h4 className="text-sm font-semibold text-gray-600">Customer Information</h4>
                                    <div className="space-y-3">
                                    {(selectedTransaction.bill_to_forename || selectedTransaction.bill_to_surname) && (
                                        <div className="p-3 bg-white border border-gray-200 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <User className="w-5 h-5 text-gray-400" />
                                            <div>
                                            <div className="text-sm font-medium text-gray-900">
                                                {formatField(selectedTransaction.bill_to_forename)} {formatField(selectedTransaction.bill_to_surname)}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {formatField(selectedTransaction.bill_to_forename || selectedTransaction.bill_to_surname, 'Name provided')}
                                            </div>
                                            </div>
                                        </div>
                                        </div>
                                    )}
                                    {selectedTransaction.bill_to_email && (
                                        <div className="p-3 bg-white border border-gray-200 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <Mail className="w-5 h-5 text-gray-400" />
                                            <div>
                                            <div className="text-sm font-medium text-gray-900">
                                                {formatField(selectedTransaction.bill_to_email, 'Email not provided')}
                                            </div>
                                            </div>
                                        </div>
                                        </div>
                                    )}
                                    </div>
                                </div>

                                {/* Merchant Information */}
                                <div className="space-y-4">
                                    <h4 className="text-sm font-semibold text-gray-600">Merchant Information</h4>
                                    <div className="space-y-3">
                                    <div className="p-3 bg-white border border-gray-200 rounded-lg">
                                        <div className="flex items-center gap-3">
                                        <Hash className="w-5 h-5 text-gray-400" />
                                        <div>
                                            <div className="text-sm text-gray-600">Merchant Code</div>
                                            <div className="text-sm font-medium text-gray-900">
                                            {selectedTransaction.merchant_code}
                                            </div>
                                        </div>
                                        </div>
                                    </div>
                                    {selectedTransaction.reason_code && (
                                        <div className="p-3 bg-white border border-gray-200 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <Info className="w-5 h-5 text-gray-400" />
                                            <div>
                                            <div className="text-sm text-gray-600">Reason Code</div>
                                            <div className="text-sm font-medium text-gray-900">
                                                {selectedTransaction.reason_code}
                                            </div>
                                            </div>
                                        </div>
                                        </div>
                                    )}
                                    </div>
                                </div>

                                {/* Transaction Details */}
                                <div className="space-y-4">
                                    <h4 className="text-sm font-semibold text-gray-600">Transaction Details</h4>
                                    <div className="space-y-2">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-600">Currency</span>
                                        <span className="font-medium text-gray-900">{selectedTransaction.currency}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-600">Status</span>
                                        <span className={`font-medium ${getStatusColor(selectedTransaction.status).split(' ')[1]}`}>
                                        {selectedTransaction.status}
                                        </span>
                                    </div>
                                    <div className="text-xs text-gray-500 mt-4">
                                        <div>Transaction ID: {selectedTransaction.id}</div>
                                        <div className="mt-1">Created: {new Date(selectedTransaction.created_at).toLocaleString()}</div>
                                    </div>
                                    </div>
                                </div>
                                </div>
                            </div>
                            </div>

                            {/* Footer */}
                            <div className="px-8 py-6 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                            <div className="flex justify-between items-center">
                                <div className="text-sm text-gray-500">
                                Transaction created on {formatDate(selectedTransaction.created_at)}
                                </div>
                                <div className="flex gap-4">
                                <button
                                    onClick={() => handleCopyToClipboard(JSON.stringify(selectedTransaction, null, 2))}
                                    className="px-5 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-white transition-colors flex items-center gap-2 shadow-sm hover:shadow"
                                >
                                    <Copy className="w-4 h-4" />
                                    Copy Details
                                </button>
                                <button
                                    onClick={() => setShowDetailsModal(false)}
                                    className="px-5 py-2.5 bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl"
                                >
                                    Close Details
                                </button>
                                </div>
                            </div>
                            </div>
                        </div>
                        </div>
                    </div>
                    )}
                  </div>
                </div>
              )}

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="px-8 py-6 border-t border-gray-200 bg-gray-50">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="text-sm text-gray-700">
                      Showing <span className="font-semibold">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                      <span className="font-semibold">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> of{' '}
                      <span className="font-semibold">{pagination.total}</span> results
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => fetchTransactions(pagination.page - 1)}
                        disabled={pagination.page === 1}
                        className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white transition-colors flex items-center gap-2"
                      >
                        Previous
                      </button>
                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                          const pageNum = i + 1;
                          return (
                            <button
                              key={pageNum}
                              onClick={() => fetchTransactions(pageNum)}
                              className={`w-10 h-10 flex items-center justify-center rounded-lg ${pagination.page === pageNum
                                  ? 'bg-gradient-to-r from-slate-800 to-slate-900 text-white'
                                  : 'border border-gray-300 hover:bg-white'
                                }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      <button
                        onClick={() => fetchTransactions(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages}
                        className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white transition-colors flex items-center gap-2"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            Need help? Contact our support team at support@merchant.com
          </p>
          <p className="text-xs text-gray-400 mt-2">
            Last refreshed: {new Date().toLocaleString()}
          </p>
        </div>
    </div>
  );
};

export default CardTransactions;